import React, { useEffect, useState } from 'react';
import { Copy, X, Download, Pencil, Check, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../../services/api';

const ResumeViewer = ({ resume, onClose }) => {
  const { fileName } = resume;
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!', {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };
  const [resumeDetails, setResumeDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [content, setContent] = useState(null);

  // ── Edit state ────────────────────────────────────────────────────────────
  const [editingName, setEditingName]       = useState(false);
  const [editName, setEditName]             = useState('');
  const [editingContact, setEditingContact] = useState(false);
  const [editContact, setEditContact]       = useState({ email: '', phone: '' });
  const [editingSkills, setEditingSkills]   = useState(false);
  const [editSkills, setEditSkills]         = useState([]);
  const [newSkill, setNewSkill]             = useState('');
  const [editingExp, setEditingExp]         = useState(false);
  const [editExp, setEditExp]               = useState([]);
  const [editingEdu, setEditingEdu]         = useState(false);
  const [editEdu, setEditEdu]               = useState([]);
  const [notes, setNotes]                   = useState('');
  const [editingNotes, setEditingNotes]     = useState(false);
  const [saving, setSaving]                 = useState(false);

  useEffect(() => {
    const fetchResumeDetails = async () => {
      try {
        const response = await api.get(`/resumes/details/${resume._id}`);
        if (response.data.success && response.data.data) {
          const d = response.data.data;
          setResumeDetails(d);
          if (d.content && d.content.trim()) setContent(d.content);
          setNotes(d.hrNotes || '');
        } else {
          setError(new Error('Invalid response structure'));
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchResumeDetails();
  }, [resume._id]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const decodeContent = (base64Content) => {
    try { return atob(base64Content); }
    catch { return 'Unable to display resume content. The file might be in a different format.'; }
  };

  const extractResumeInfo = (content) => {
    if (!content) return null;
    try {
      const decodedContent = decodeContent(content);
      const lines = decodedContent.split('\n').map(l => l.trim()).filter(l => l);

      const emailMatch = decodedContent.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
      const email = emailMatch ? emailMatch[0] : null;
      const phoneMatch = decodedContent.match(/(\+?[\d\s\-()]{10,})/);
      const phone = phoneMatch ? phoneMatch[0] : null;

      let name = null;
      for (const line of lines) {
        if (line.length > 2 && line.length < 50 && /^[A-Z][a-z]+ [A-Z][a-z]+/.test(line)) {
          name = line; break;
        }
      }

      const experience = [];
      let inExp = false;
      for (let i = 0; i < lines.length; i++) {
        const l = lines[i].toLowerCase();
        if (['experience','work history','employment','career'].some(k => l.includes(k))) { inExp = true; continue; }
        if (inExp && ['education','skills','certification'].some(k => l.includes(k))) break;
        if (inExp) {
          const titleMatch   = lines[i].match(/^([A-Z][a-z\s]+(?:Manager|Developer|Engineer|Specialist|Analyst|Consultant|Director|Lead|Senior|Junior|Associate))/);
          const companyMatch = lines[i].match(/(?:at|with|@)\s*([A-Z][a-zA-Z\s&.,]+)/);
          const durMatch     = lines[i].match(/(\d{4}\s*[-–]\s*(?:Present|\d{4})|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s*\d{4}\s*[-–]\s*(?:Present|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s*\d{4}))/);
          if (titleMatch || companyMatch || durMatch) {
            const exp = { title: titleMatch?.[1]?.trim() || '', company: companyMatch?.[1]?.trim() || '', duration: durMatch?.[1]?.trim() || '' };
            if (exp.title || exp.company) experience.push(exp);
          }
        }
      }

      const skills = [];
      let inSkills = false;
      for (let i = 0; i < lines.length; i++) {
        const l = lines[i].toLowerCase();
        if (['skills','technologies','tools','languages','frameworks'].some(k => l.includes(k))) { inSkills = true; continue; }
        if (inSkills && ['experience','education','certification'].some(k => l.includes(k))) break;
        if (inSkills) {
          const m = lines[i].match(/[A-Z][a-zA-Z0-9+#\s]+/g);
          if (m) skills.push(...m.map(s => s.trim()).filter(s => s.length > 2));
        }
      }

      const education = [];
      let inEdu = false;
      for (let i = 0; i < lines.length; i++) {
        const l = lines[i].toLowerCase();
        if (['education','academic','degree','university','college'].some(k => l.includes(k))) { inEdu = true; continue; }
        if (inEdu && ['experience','skills','certification'].some(k => l.includes(k))) break;
        if (inEdu) {
          const degMatch  = lines[i].match(/(Bachelor|Master|PhD|B\.?Tech|M\.?Tech|MBA|B\.?Sc|M\.?Sc)/i);
          const instMatch = lines[i].match(/(University|College|Institute|School)/i);
          const yearMatch = lines[i].match(/(\d{4})/);
          if (degMatch || instMatch) {
            const edu = { degree: degMatch?.[1] || '', institution: instMatch ? lines[i].trim() : '', year: yearMatch?.[1] || '' };
            if (edu.degree || edu.institution) education.push(edu);
          }
        }
      }

      return { name, email, phone, experience: experience.slice(0, 3), skills: skills.slice(0, 10), education: education.slice(0, 2), content: decodedContent };
    } catch { return null; }
  };

  const getRelevantAIStrengths = (aiStrengths, resumeInfo) => {
    if (!aiStrengths || !Array.isArray(aiStrengths)) return [];
    const actualSkillsLower = (resumeInfo?.skills || []).map(s => s.toLowerCase());
    const actualJobTitles   = (resumeInfo?.experience || []).map(e => e.title?.toLowerCase() || '');
    const relevantKeywords  = ['marketing','digital','seo','analytics','social media','advertising','management','leadership','strategy','campaign','performance','development','programming','coding','software','technical','design','creative','content','communication','project'];
    return aiStrengths.filter(s => {
      const sl = s.toLowerCase();
      return actualSkillsLower.some(k => sl.includes(k) || k.includes(sl))
          || actualJobTitles.some(t => sl.includes(t) || t.includes(sl))
          || relevantKeywords.some(k => sl.includes(k));
    });
  };

  // ── Save helper ───────────────────────────────────────────────────────────
  const saveEdit = async (payload) => {
    setSaving(true);
    try {
      const res = await api.patch(`/resumes/${resume._id}/edit`, payload);
      if (res.data.success) {
        toast.success('Saved!', { autoClose: 1500 });
        if (payload.candidateName !== undefined) {
          setResumeDetails(d => ({ ...d, candidateName: payload.candidateName }));
        }
        if (payload.hrNotes !== undefined) setNotes(payload.hrNotes);
      } else {
        toast.error('Save failed');
      }
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Render resume content ─────────────────────────────────────────────────
  const renderContent = () => {
    if (!content) {
      return (
        <div className="flex flex-col items-center justify-center h-full space-y-4">
          <div className="text-gray-500 text-center">
            <p className="mb-4">No content available for this resume.</p>
            <p className="text-sm mb-4">The resume content could not be extracted during processing.</p>
          </div>
          {resumeDetails?.fileUrl && (
            <a href={resumeDetails.fileUrl} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600">
              <Download className="w-4 h-4" /> Download Original File
            </a>
          )}
        </div>
      );
    }
    try {
      return (
        <div className="prose max-w-none">
          <pre className="whitespace-pre-wrap text-sm">{decodeContent(content)}</pre>
        </div>
      );
    } catch {
      return <div className="text-gray-500">Unable to display content. File format may not be supported.</div>;
    }
  };

  const resumeInfo = extractResumeInfo(content);

  // resolved display values (db parsed data takes precedence over regex extraction)
  const parsedData   = resumeDetails?.analysis?.parsedData || {};
  const displayName  = resumeDetails?.candidateName || resumeInfo?.name || 'Unknown';
  const displayEmail = parsedData.email   || resumeInfo?.email || resumeDetails?.email || '';
  const displayPhone = parsedData.phone   || resumeInfo?.phone || '';
  const displaySkills = (parsedData.skills?.length ? parsedData.skills : resumeInfo?.skills) || [];
  const displayExp    = (parsedData.experience?.length ? parsedData.experience : resumeInfo?.experience) || [];
  const displayEdu    = (parsedData.education?.length ? parsedData.education : resumeInfo?.education) || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-7xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-semibold">Resume Details</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex h-[calc(90vh-4rem)] overflow-hidden">
          {/* Left Panel - Resume Content */}
          <div className="w-3/5 border-r overflow-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent"></div>
              </div>
            ) : renderContent()}
          </div>

          {/* Right Panel */}
          <div className="w-2/5 overflow-y-auto p-6">
            {loading ? (
              <div className="animate-pulse space-y-4">
                <div className="h-20 bg-gray-200 rounded"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                </div>
              </div>
            ) : error ? (
              <div className="text-red-500">Error loading resume details</div>
            ) : (
              <>
                {/* ── Profile / Name ───────────────────────────────────── */}
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center text-orange-500 text-xl font-medium flex-shrink-0">
                    {displayName?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    {editingName ? (
                      <div className="flex items-center gap-2">
                        <input
                          autoFocus
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          className="flex-1 border border-orange-400 rounded px-2 py-1 text-sm focus:outline-none"
                        />
                        <button disabled={saving}
                          onClick={async () => {
                            await saveEdit({ candidateName: editName });
                            setEditingName(false);
                          }}
                          className="p-1 text-green-600 hover:bg-green-50 rounded">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingName(false)} className="p-1 text-gray-400 hover:bg-gray-100 rounded">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <h3 className="text-xl font-semibold truncate">{displayName}</h3>
                        <button
                          onClick={() => { setEditName(displayName); setEditingName(true); }}
                          className="p-1 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors flex-shrink-0"
                          title="Edit name">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    <p className="text-gray-600 text-sm truncate">{fileName}</p>
                  </div>
                </div>

                {/* ── Test Results ─────────────────────────────────────── */}
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-4">Test Results</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {resumeDetails?.preScreeningResult && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-500 mb-1">Pre-Screening</div>
                        <div className="font-medium">{resumeDetails.preScreeningResult?.score || 0}%</div>
                        <div className="text-xs text-gray-500">
                          {resumeDetails.preScreeningResult.correctAnswers} / {resumeDetails.preScreeningResult.totalQuestions} correct
                        </div>
                      </div>
                    )}
                    {resumeDetails?.psychoanalyticalResult && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-500 mb-1">Psychoanalytical score</div>
                        <div className="font-medium">{resumeDetails.psychoanalyticalResult?.score || 0}%</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── AI Scores ────────────────────────────────────────── */}
                {(resumeDetails?.analysis?.scores || resumeDetails?.aiScore !== undefined || resumeDetails?.matchScore !== undefined) && (
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-4">AI Evaluation Scores</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-500 mb-1">ATS Score</div>
                        <div className="font-medium">{resumeDetails?.analysis?.scores?.atsScore !== undefined ? `${resumeDetails.analysis.scores.atsScore}%` : `${resumeDetails?.aiScore || 0}%`}</div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-500 mb-1">JD Match</div>
                        <div className="font-medium">{resumeDetails?.analysis?.scores?.jdMatch !== undefined ? `${resumeDetails.analysis.scores.jdMatch}/100` : `${resumeDetails?.matchScore || 0}/100`}</div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-500 mb-1">Skills Match</div>
                        <div className="font-medium">{resumeDetails?.analysis?.scores?.skillsMatch !== undefined ? `${resumeDetails.analysis.scores.skillsMatch}/100` : `${resumeDetails?.matchScore || 0}/100`}</div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-500 mb-1">Experience Match</div>
                        <div className="font-medium">{resumeDetails?.analysis?.scores?.experienceMatch !== undefined ? `${resumeDetails.analysis.scores.experienceMatch}/100` : `${resumeDetails?.matchScore || 0}/100`}</div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-500 mb-1">Overall Score</div>
                        <div className="font-medium">{resumeDetails?.analysis?.scores?.overallScore !== undefined ? `${resumeDetails.analysis.scores.overallScore}%` : `${Math.round(((resumeDetails?.aiScore || 0) + (resumeDetails?.matchScore || 0)) / 2)}%`}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Experience ───────────────────────────────────────── */}
                {(displayExp.length > 0 || editingExp) && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-900">Experience</h4>
                      {!editingExp ? (
                        <button onClick={() => { setEditExp(displayExp.map(e => ({ ...e }))); setEditingExp(true); }}
                          className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600">
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                      ) : (
                        <div className="flex gap-2">
                          <button disabled={saving}
                            onClick={async () => {
                              await saveEdit({ parsedData: { experience: editExp } });
                              setResumeDetails(d => ({ ...d, analysis: { ...d.analysis, parsedData: { ...d.analysis?.parsedData, experience: editExp } } }));
                              setEditingExp(false);
                            }}
                            className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700">
                            <Check className="w-3 h-3" /> Save
                          </button>
                          <button onClick={() => setEditingExp(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      {(editingExp ? editExp : displayExp).map((exp, i) => (
                        <div key={i} className="bg-gray-50 p-3 rounded-lg">
                          {editingExp ? (
                            <div className="space-y-2">
                              <input value={exp.title} placeholder="Job title"
                                onChange={e => setEditExp(prev => prev.map((x, j) => j === i ? { ...x, title: e.target.value } : x))}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-orange-400" />
                              <input value={exp.company} placeholder="Company"
                                onChange={e => setEditExp(prev => prev.map((x, j) => j === i ? { ...x, company: e.target.value } : x))}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-orange-400" />
                              <div className="flex gap-2">
                                <input value={exp.duration} placeholder="Duration"
                                  onChange={e => setEditExp(prev => prev.map((x, j) => j === i ? { ...x, duration: e.target.value } : x))}
                                  className="flex-1 border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-orange-400" />
                                <button onClick={() => setEditExp(prev => prev.filter((_, j) => j !== i))}
                                  className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {exp.title    && <div className="font-medium text-sm">{exp.title}</div>}
                              {exp.company  && <div className="text-sm text-gray-600">{exp.company}</div>}
                              {exp.duration && <div className="text-xs text-gray-500">{exp.duration}</div>}
                            </>
                          )}
                        </div>
                      ))}
                      {editingExp && (
                        <button onClick={() => setEditExp(prev => [...prev, { title: '', company: '', duration: '' }])}
                          className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600">
                          <Plus className="w-3 h-3" /> Add entry
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Skills ───────────────────────────────────────────── */}
                {(displaySkills.length > 0 || editingSkills) && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-900">Skills</h4>
                      {!editingSkills ? (
                        <button onClick={() => { setEditSkills([...displaySkills]); setEditingSkills(true); }}
                          className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600">
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                      ) : (
                        <div className="flex gap-2">
                          <button disabled={saving}
                            onClick={async () => {
                              await saveEdit({ parsedData: { skills: editSkills } });
                              setResumeDetails(d => ({ ...d, analysis: { ...d.analysis, parsedData: { ...d.analysis?.parsedData, skills: editSkills } } }));
                              setEditingSkills(false);
                            }}
                            className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700">
                            <Check className="w-3 h-3" /> Save
                          </button>
                          <button onClick={() => setEditingSkills(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(editingSkills ? editSkills : displaySkills).map((skill, i) => (
                        <span key={i} className="flex items-center gap-1 px-3 py-1 bg-orange-50 text-orange-600 rounded-full text-sm">
                          {skill}
                          {editingSkills && (
                            <button onClick={() => setEditSkills(prev => prev.filter((_, j) => j !== i))}
                              className="ml-1 text-orange-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                          )}
                        </span>
                      ))}
                    </div>
                    {editingSkills && (
                      <div className="flex gap-2 mt-3">
                        <input value={newSkill} onChange={e => setNewSkill(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && newSkill.trim()) { setEditSkills(p => [...p, newSkill.trim()]); setNewSkill(''); } }}
                          placeholder="Add skill, press Enter"
                          className="flex-1 border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-orange-400" />
                        <button onClick={() => { if (newSkill.trim()) { setEditSkills(p => [...p, newSkill.trim()]); setNewSkill(''); } }}
                          className="px-3 py-1 bg-orange-500 text-white rounded text-sm hover:bg-orange-600">Add</button>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Education ────────────────────────────────────────── */}
                {(displayEdu.length > 0 || editingEdu) && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-900">Education</h4>
                      {!editingEdu ? (
                        <button onClick={() => { setEditEdu(displayEdu.map(e => ({ ...e }))); setEditingEdu(true); }}
                          className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600">
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                      ) : (
                        <div className="flex gap-2">
                          <button disabled={saving}
                            onClick={async () => {
                              await saveEdit({ parsedData: { education: editEdu } });
                              setResumeDetails(d => ({ ...d, analysis: { ...d.analysis, parsedData: { ...d.analysis?.parsedData, education: editEdu } } }));
                              setEditingEdu(false);
                            }}
                            className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700">
                            <Check className="w-3 h-3" /> Save
                          </button>
                          <button onClick={() => setEditingEdu(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      {(editingEdu ? editEdu : displayEdu).map((edu, i) => (
                        <div key={i} className="bg-gray-50 p-3 rounded-lg">
                          {editingEdu ? (
                            <div className="space-y-2">
                              <input value={edu.degree} placeholder="Degree"
                                onChange={e => setEditEdu(prev => prev.map((x, j) => j === i ? { ...x, degree: e.target.value } : x))}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-orange-400" />
                              <input value={edu.institution} placeholder="Institution"
                                onChange={e => setEditEdu(prev => prev.map((x, j) => j === i ? { ...x, institution: e.target.value } : x))}
                                className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-orange-400" />
                              <div className="flex gap-2">
                                <input value={edu.year} placeholder="Year"
                                  onChange={e => setEditEdu(prev => prev.map((x, j) => j === i ? { ...x, year: e.target.value } : x))}
                                  className="flex-1 border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:border-orange-400" />
                                <button onClick={() => setEditEdu(prev => prev.filter((_, j) => j !== i))}
                                  className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {edu.degree      && <div className="font-medium text-sm">{edu.degree}</div>}
                              {edu.institution && <div className="text-sm text-gray-600">{edu.institution}</div>}
                              {edu.year        && <div className="text-xs text-gray-500">{edu.year}</div>}
                            </>
                          )}
                        </div>
                      ))}
                      {editingEdu && (
                        <button onClick={() => setEditEdu(prev => [...prev, { degree: '', institution: '', year: '' }])}
                          className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600">
                          <Plus className="w-3 h-3" /> Add entry
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* ── AI Strengths ─────────────────────────────────────── */}
                {resumeDetails?.analysis?.highlightedStrengths?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-4">AI Analysis - Key Strengths</h4>
                    {getRelevantAIStrengths(resumeDetails.analysis.highlightedStrengths, resumeInfo).length === 0 && (
                      <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-700">⚠️ AI analysis may not be relevant to this resume.</p>
                      </div>
                    )}
                    <ul className="space-y-2">
                      {getRelevantAIStrengths(resumeDetails.analysis.highlightedStrengths, resumeInfo).map((s, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-orange-500">•</span>
                          <span className="text-gray-600 text-sm">{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* ── Contact Information ──────────────────────────────── */}
                <div className="mb-6 pt-4 border-t">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-gray-900">Contact Information</h4>
                    {!editingContact ? (
                      <button onClick={() => { setEditContact({ email: displayEmail, phone: displayPhone }); setEditingContact(true); }}
                        className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600">
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button disabled={saving}
                          onClick={async () => {
                            await saveEdit({ email: editContact.email, parsedData: { phone: editContact.phone, email: editContact.email } });
                            setResumeDetails(d => ({ ...d, email: editContact.email, analysis: { ...d.analysis, parsedData: { ...d.analysis?.parsedData, phone: editContact.phone, email: editContact.email } } }));
                            setEditingContact(false);
                          }}
                          className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700">
                          <Check className="w-3 h-3" /> Save
                        </button>
                        <button onClick={() => setEditingContact(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    {editingContact ? (
                      <>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">Email</label>
                          <input value={editContact.email} onChange={e => setEditContact(c => ({ ...c, email: e.target.value }))}
                            className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-orange-400" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">Phone</label>
                          <input value={editContact.phone} onChange={e => setEditContact(c => ({ ...c, phone: e.target.value }))}
                            className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-orange-400" />
                        </div>
                      </>
                    ) : (
                      <>
                        {displayEmail && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Email:</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{displayEmail}</span>
                              <button onClick={() => copyToClipboard(displayEmail)} className="p-1 hover:bg-gray-100 rounded">
                                <Copy className="w-4 h-4 text-gray-500" />
                              </button>
                            </div>
                          </div>
                        )}
                        {displayPhone && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Phone:</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{displayPhone}</span>
                              <button onClick={() => copyToClipboard(displayPhone)} className="p-1 hover:bg-gray-100 rounded">
                                <Copy className="w-4 h-4 text-gray-500" />
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* ── HR Notes ─────────────────────────────────────────── */}
                <div className="mb-6 pt-4 border-t">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">HR Notes</h4>
                    {!editingNotes ? (
                      <button onClick={() => setEditingNotes(true)}
                        className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600">
                        <Pencil className="w-3 h-3" /> {notes ? 'Edit' : 'Add note'}
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button disabled={saving}
                          onClick={async () => { await saveEdit({ hrNotes: notes }); setEditingNotes(false); }}
                          className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700">
                          <Check className="w-3 h-3" /> Save
                        </button>
                        <button onClick={() => setEditingNotes(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                      </div>
                    )}
                  </div>
                  {editingNotes ? (
                    <textarea
                      autoFocus
                      rows={4}
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                      placeholder="Add notes about this candidate…"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400 resize-none"
                    />
                  ) : notes ? (
                    <p className="text-sm text-gray-700 bg-yellow-50 border border-yellow-100 rounded-lg p-3 whitespace-pre-wrap">{notes}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">No notes yet.</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeViewer;
